// Animated Horizontal Timeline (ES Module)
// Uses global gsap, ScrollTrigger, and Lenis from window object

document.addEventListener("DOMContentLoaded", () => {
     if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
          console.warn('⚠️ Animated Timeline: GSAP or ScrollTrigger not loaded');
          return;
     }
     
     gsap.registerPlugin(ScrollTrigger);

     const lenis = new Lenis();
     lenis.on("scroll", ScrollTrigger.update);
     gsap.ticker.add((time) => {
          lenis.raf(time * 1000);
     });
     gsap.ticker.lagSmoothing(0);

     const stickySection = document.querySelector(".animated-timeline-sticky");
     if (!stickySection) return;

     const outlineCanvas = document.querySelector(".animated-timeline-outline-layer");
     const fillCanvas = document.querySelector(".animated-timeline-fill-layer");
     const outlineCtx = outlineCanvas.getContext("2d");
     const fillCtx = fillCanvas.getContext("2d");

     function setCanvasSize(canvas, ctx) {
          const dpr = window.devicePixelRatio || 1;
          canvas.width = window.innerWidth * dpr;
          canvas.height = window.innerHeight * dpr;
          canvas.style.width = `${window.innerWidth}px`;
          canvas.style.height = `${window.innerHeight}px`;
          ctx.scale(dpr, dpr);
     }
     setCanvasSize(outlineCanvas, outlineCtx);
     setCanvasSize(fillCanvas, fillCtx);

     const triangleSize = 150;
     const lineWidth = 1;
     const SCALE_THRESHOLD = 0.01;
     const triangleStates = new Map();
     let animationFrameId = null;
     let canvasXPosition = 0;
     let currentScrollTrigger = null;

     function drawTriangle(ctx, x, y, fillScale = 0, flipped = false) {
          const halfSize = triangleSize / 2;

          if (fillScale < SCALE_THRESHOLD) {
               ctx.beginPath();
               if (!flipped) {
                    ctx.moveTo(x, y - halfSize);
                    ctx.lineTo(x + halfSize, y + halfSize);
                    ctx.lineTo(x - halfSize, y + halfSize);
               } else {
                    ctx.moveTo(x, y + halfSize);
                    ctx.lineTo(x + halfSize, y - halfSize);
                    ctx.lineTo(x - halfSize, y - halfSize);
               }
               ctx.closePath();
               const surfaceColor = getComputedStyle(document.documentElement).getPropertyValue('--color-surface-primary').trim() || '#ffffff';
               ctx.strokeStyle = surfaceColor.replace('rgb', 'rgba').replace(')', ', 0.075)');
               ctx.lineWidth = lineWidth;
               ctx.stroke();
          }

          if (fillScale >= SCALE_THRESHOLD) {
               ctx.save();
               ctx.translate(x, y);
               ctx.scale(fillScale, fillScale);
               ctx.translate(-x, -y);

               ctx.beginPath();
               if (!flipped) {
                    ctx.moveTo(x, y - halfSize);
                    ctx.lineTo(x + halfSize, y + halfSize);
                    ctx.lineTo(x - halfSize, y + halfSize);
               } else {
                    ctx.moveTo(x, y + halfSize);
                    ctx.lineTo(x + halfSize, y - halfSize);
                    ctx.lineTo(x - halfSize, y - halfSize);
               }
               ctx.closePath();
               const triangleColor = getComputedStyle(document.documentElement).getPropertyValue('--color-tertiary-accent').trim() || '#ff6b00';
               ctx.fillStyle = triangleColor;
               ctx.strokeStyle = triangleColor;
               ctx.lineWidth = lineWidth;
               ctx.stroke();
               ctx.fill();
               ctx.restore();
          }
     }

     function drawGrid(scrollProgress = 0) {
          if (animationFrameId) {
               cancelAnimationFrame(animationFrameId);
          }

          outlineCtx.clearRect(0, 0, outlineCanvas.width, outlineCanvas.height);
          fillCtx.clearRect(0, 0, fillCanvas.width, fillCanvas.height);

          const triangleStartProgress = typeof progressDivisor !== 'undefined' ? progressDivisor : 0.42;
          const triangleDuration = 1 - triangleStartProgress;
          const animationProgress =
               scrollProgress <= triangleStartProgress ? 0 :
                    (scrollProgress - triangleStartProgress) / triangleDuration;

          let needsUpdate = false;
          const animationSpeed = 0.15;

          triangleStates.forEach((state, key) => {
               if (state.scale < 1) {
                    const x =
                         state.col * (triangleSize * 0.5) + triangleSize / 2 + canvasXPosition;
                    const y = state.row * triangleSize + triangleSize / 2;
                    const flipped = (state.row + state.col) % 2 !== 0;
                    drawTriangle(outlineCtx, x, y, 0, flipped);
               }
          });

          triangleStates.forEach((state, key) => {
               const shouldBeVisible = state.order <= animationProgress;
               const targetScale = shouldBeVisible ? 1 : 0;
               const newScale =
                    state.scale + (targetScale - state.scale) * animationSpeed;

               if (Math.abs(newScale - state.scale) > 0.001) {
                    state.scale = newScale;
                    needsUpdate = true;
               }

               if (state.scale >= SCALE_THRESHOLD) {
                    const x =
                         state.col * (triangleSize * 0.5) + triangleSize / 2 + canvasXPosition;
                    const y = state.row * triangleSize + triangleSize / 2;
                    const flipped = (state.row + state.col) % 2 !== 0;
                    drawTriangle(fillCtx, x, y, state.scale, flipped);
               }
          });

          if (needsUpdate) {
               animationFrameId = requestAnimationFrame(() => drawGrid(scrollProgress));
          }
     }

     function initializeTriangles() {
          const cols = Math.ceil(window.innerWidth / (triangleSize * 0.5));
          const rows = Math.ceil(window.innerHeight / (triangleSize * 0.5));
          const totalTriangles = rows * cols;

          const positions = [];
          for (let r = 0; r < rows; r++) {
               for (let c = 0; c < cols; c++) {
                    positions.push({ row: r, col: c, key: `${r}-${c}` });
               }
          }

          for (let i = positions.length - 1; i > 0; i--) {
               const j = Math.floor(Math.random() * (i + 1));
               [positions[i], positions[j]] = [positions[j], positions[i]];
          }

          positions.forEach((pos, index) => {
               triangleStates.set(pos.key, {
                    order: index / totalTriangles,
                    scale: 0,
                    row: pos.row,
                    col: pos.col,
               });
          });
     }

     function initTimeline() {
          const cards = document.querySelectorAll(".animated-timeline-card");
          const cardCount = cards.length;

          const SCROLL_HEIGHT_BASE = 6;
          const SCROLL_HEIGHT_SCALE = 0.8;
          const PROGRESS_BASE = 0.42;
          const PROGRESS_SCALE = 0.035;

          const scrollMultiplier = Math.min(cardCount * SCROLL_HEIGHT_SCALE, 9);
          const stickyHeight = window.innerHeight * (SCROLL_HEIGHT_BASE + scrollMultiplier);

          const progressAdjustment = Math.max(0.02, (10 - cardCount) * PROGRESS_SCALE);
          let progressDivisor = PROGRESS_BASE + progressAdjustment;

          const isMobile = window.innerWidth < 900;
          const isVerySmallMobile = window.innerWidth < 480;

          let mobileProgressAdjustment = 1.0;
          if (isVerySmallMobile) {
               mobileProgressAdjustment = 0.65;
          } else if (isMobile) {
               mobileProgressAdjustment = 0.85;
          }

          progressDivisor = progressDivisor * mobileProgressAdjustment;

          const baseWidthMultiplier = cardCount * 1.2;
          let containerWidthPercent = 400 + (baseWidthMultiplier * 50);

          if (isVerySmallMobile) {
               containerWidthPercent = Math.max(containerWidthPercent, 900);
          } else if (isMobile) {
               containerWidthPercent = Math.max(containerWidthPercent, 650);
          }

          const cardsContainer = document.querySelector(".animated-timeline-cards");
          if (!cardsContainer) {
               console.error("❌ Cards container not found!");
               return;
          }

          cardsContainer.style.width = containerWidthPercent + '%';

          if (currentScrollTrigger) {
               currentScrollTrigger.kill();
          }

          let header = document.querySelector('.site-header');
          if (!header) {
               setTimeout(() => {
                    header = document.querySelector('.site-header');
               }, 100);
          }

          currentScrollTrigger = ScrollTrigger.create({
               trigger: stickySection,
               start: "top top",
               end: `+=${stickyHeight}px`,
               pin: true,
               onEnter: () => {
                    const currentHeader = document.querySelector('.site-header') || header;
                    if (currentHeader) {
                         currentHeader.style.opacity = '0.1';
                         currentHeader.style.pointerEvents = 'none';
                    }
               },
               onLeave: () => {
                    const currentHeader = document.querySelector('.site-header') || header;
                    if (currentHeader) {
                         currentHeader.style.opacity = '';
                         currentHeader.style.pointerEvents = '';
                    }
               },
               onEnterBack: () => {
                    const currentHeader = document.querySelector('.site-header') || header;
                    if (currentHeader) {
                         currentHeader.style.opacity = '0.1';
                         currentHeader.style.pointerEvents = 'none';
                    }
               },
               onLeaveBack: () => {
                    const currentHeader = document.querySelector('.site-header') || header;
                    if (currentHeader) {
                         currentHeader.style.opacity = '';
                         currentHeader.style.pointerEvents = '';
                    }
               },
               onUpdate: (self) => {
                    canvasXPosition = -self.progress * 200;
                    drawGrid(self.progress);

                    const progress = Math.min(self.progress / progressDivisor, 1);

                    const totalScrollDistance = (containerWidthPercent / 100) * window.innerWidth;
                    const availableScrollDistance = totalScrollDistance - window.innerWidth;
                    const movement = progress * availableScrollDistance;

                    gsap.set(cardsContainer, {
                         x: -movement,
                    });
               },
          });
     }

     initializeTriangles();
     drawGrid();
     initTimeline();

     window.addEventListener("resize", () => {
          clearTimeout(window.timelineResizeTimeout);
          window.timelineResizeTimeout = setTimeout(() => {
               setCanvasSize(outlineCanvas, outlineCtx);
               setCanvasSize(fillCanvas, fillCtx);
               triangleStates.clear();
               initializeTriangles();
               drawGrid();
               initTimeline();
          }, 250);
     });
     
     console.log('✅ Animated Horizontal Timeline initialized');
});

export default {};
